import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Truck, RefreshCw, Printer, AlertTriangle, CalendarDays, Phone } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BUCKETS = [
  { key: 'breached', label: 'Breached', cls: 'border-red-500/50 bg-red-500/10 text-red-400' },
  { key: 'today', label: 'Due Today', cls: 'border-amber-500/50 bg-amber-500/10 text-amber-400' },
  { key: 'tomorrow', label: 'Due Tomorrow', cls: 'border-yellow-500/40 bg-yellow-500/10 text-yellow-300' },
  { key: 'upcoming', label: 'Next 7 Days', cls: 'border-sky-500/40 bg-sky-500/10 text-sky-300' },
  { key: 'later', label: 'Later', cls: 'border-wt-border bg-wt-subtle text-wt-text-secondary' },
];

const fmtDate = (s) => s ? new Date(s + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—';
const statusLabel = (s) => (s || '').replace(/_/g, ' ');

export default function DeliveryTrackSheetPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeBucket, setActiveBucket] = useState('today');

  const load = async (isFirst = false) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/delivery/track-sheet`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
      if (isFirst && (res.data.counts?.today || 0) === 0 && (res.data.counts?.breached || 0) > 0) {
        setActiveBucket('breached');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load track sheet');
      setData({ counts: {}, buckets: {} });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(true); }, []);

  const rows = data?.buckets?.[activeBucket] || [];

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="delivery-track-sheet-page">
      <div className="flex items-center justify-between flex-wrap gap-3 print:hidden">
        <div>
          <h1 className="text-xl font-semibold text-wt-text-primary flex items-center gap-2">
            <Truck className="w-5 h-5 text-wt-gold" />
            Delivery Track Sheet
          </h1>
          <p className="text-sm text-wt-text-secondary mt-0.5">
            Active orders by delivery due date · today is {fmtDate(data?.today)}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => load()} disabled={loading}
            className="border-wt-border text-wt-text-primary" data-testid="track-sheet-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Refresh
          </Button>
          <Button variant="outline" onClick={() => window.print()}
            className="border-wt-border text-wt-text-primary" data-testid="track-sheet-print-btn">
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap print:hidden">
        {BUCKETS.map((b) => (
          <button key={b.key}
            onClick={() => setActiveBucket(b.key)}
            className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-colors ${b.cls} ${activeBucket === b.key ? 'ring-2 ring-wt-gold/60' : 'opacity-70 hover:opacity-100'}`}
            data-testid={`track-sheet-tab-${b.key}`}>
            {b.key === 'breached' && <AlertTriangle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />}
            {b.label} ({data?.counts?.[b.key] ?? 0})
          </button>
        ))}
      </div>

      <div className="hidden print:block mb-2">
        <h2 className="text-lg font-bold">Delivery Track Sheet — {BUCKETS.find(b => b.key === activeBucket)?.label} ({fmtDate(data?.today)})</h2>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-wt-text-muted" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-wt-text-muted" data-testid="track-sheet-empty">
          <CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-40" />
          No orders in this bucket
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-wt-border">
          <table className="w-full text-sm" data-testid="track-sheet-table">
            <thead>
              <tr className="bg-wt-subtle text-wt-text-secondary text-left text-xs uppercase tracking-wider">
                <th className="px-3 py-2.5">Due</th>
                <th className="px-3 py-2.5">Order</th>
                <th className="px-3 py-2.5">Customer</th>
                <th className="px-3 py-2.5">City</th>
                <th className="px-3 py-2.5">Items</th>
                <th className="px-3 py-2.5">Amount</th>
                <th className="px-3 py-2.5">Driver / Courier</th>
                <th className="px-3 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}
                  className="border-t border-wt-border hover:bg-wt-subtle/60 cursor-pointer"
                  onClick={() => navigate(`/orders?openOrder=${r.id}`)}
                  data-testid={`track-sheet-row-${r.order_number}`}>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <span className={r.promised ? 'text-wt-gold font-semibold' : 'text-wt-text-primary'}>
                      {fmtDate(r.date)}
                    </span>
                    {r.promised && <Badge className="ml-1.5 bg-wt-gold/15 text-wt-gold border border-wt-gold/30 text-[10px]">Promised</Badge>}
                    {activeBucket === 'breached' && data?.today && r.date >= data.today && (
                      <Badge className="ml-1.5 bg-red-500/15 text-red-400 border border-red-500/30 text-[10px]" title="Original promise was missed — estimate rolled forward">Delayed</Badge>
                    )}
                    {r.basis === 'production' && <Badge className="ml-1.5 bg-purple-500/15 text-purple-300 border border-purple-500/30 text-[10px]">Production</Badge>}
                  </td>
                  <td className="px-3 py-2.5 font-medium text-wt-text-primary whitespace-nowrap">{r.order_number}</td>
                  <td className="px-3 py-2.5">
                    <div className="text-wt-text-primary">{r.customer_name || '—'}</div>
                    {r.customer_phone && (
                      <div className="text-xs text-wt-text-muted flex items-center gap-1"><Phone className="w-3 h-3" />{r.customer_phone}</div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-wt-text-secondary">{r.city || '—'}</td>
                  <td className="px-3 py-2.5 text-wt-text-secondary">{r.items_count}</td>
                  <td className="px-3 py-2.5 text-wt-text-primary whitespace-nowrap">{Number(r.total_amount || 0).toFixed(2)}</td>
                  <td className="px-3 py-2.5 text-wt-text-secondary capitalize">{r.driver || r.courier || '—'}</td>
                  <td className="px-3 py-2.5">
                    <span className="capitalize text-xs px-2 py-0.5 rounded-full border border-wt-border bg-wt-subtle text-wt-text-secondary">
                      {statusLabel(r.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
